import { getCountdownUnitsFromSeconds } from '@amusnet/react-ui'
import { CountdownCounterDisplayItems, CountdownCounterValues } from '@components/CountdownFreeSpinsBonus/types'
import { BonusImages } from '@constants/bonusSystem'
import { AtlasMapValue, BuyBonusFeature } from 'game-ui-types'
import { CountdownStrategy, BonusType } from '@amusnet/bonuses-http-client'
import { Bonus, FreeBuyBonusBonus } from '@amusnet/mgs-ws-client'
type AtlasElement = { atlasName: string; value: AtlasMapValue }

/**
 * Resolves the free-buy-bonus card image, tolerating the inconsistent FBB frame naming across game
 * atlases. Games ship different sets in `buyBonusExternalUI`: some have the per-feature numbered card
 * (`free-buy-bonus-image-card-01.png`), some only the plain `free-buy-bonus-image-card.png` — but every
 * atlas has `free-buy-bonus-image-card-bundle.png`. Try the configured name, then the plain card, then
 * the bundle as a universal last resort, so a single awarded bonus always shows artwork instead of an
 * empty box (e.g. 405 ships `-01`, 611 ships only the plain card).
 */
export const resolveFreeBuyBonusImage = (
    findElementByImageName: (name: string) => AtlasElement | undefined,
    configuredImageName?: string
): AtlasElement | undefined => {
    const candidates = [configuredImageName, BonusImages.FREE_BUY_BONUS_MAIN, BonusImages.FREE_BUY_BONUS_BUNDLE].filter(
        (name): name is string => Boolean(name)
    )

    for (const name of candidates) {
        const element = findElementByImageName(name)

        if (element) return element
    }

    return undefined
}

const getBonusProp = <K extends keyof BuyBonusFeature>(
    bonus: Bonus,
    features: BuyBonusFeature[],
    prop: K
): BuyBonusFeature[K] | '' => {
    const bonusLabel = (bonus as FreeBuyBonusBonus).typeConfiguration?.freeBuyBonusConfiguration?.featureId

    if (!bonusLabel) {
        return ''
    }

    const feature = features.find((f) => f.guaranteedName === bonusLabel || f.name === bonusLabel)

    return feature ? feature[prop] : ''
}

export const getFBBDetails = (
    bonus: Bonus,
    features: BuyBonusFeature[]
):
    | {
          label: string
          name: string
          imageName: string
          multiplier: number | string
          guaranteedName: string | undefined
          guaranteedMultiplier: number | string | undefined
      }
    | undefined => {
    if (!(bonus as FreeBuyBonusBonus).typeConfiguration?.freeBuyBonusConfiguration) {
        return
    }

    const label = getBonusProp(bonus, features, 'label')
    const name = getBonusProp(bonus, features, 'name')
    const multiplier = getBonusProp(bonus, features, 'multiplier')
    const guaranteedName = getBonusProp(bonus, features, 'guaranteedName')
    const guaranteedMultiplier = getBonusProp(bonus, features, 'guaranteedMultiplier')
    const featureIndex = features.findIndex((f) => f.label === label)
    const imageName =
        features.length > 1 ? `free-buy-bonus-image-card-0${featureIndex + 1}.png` : BonusImages.FREE_BUY_BONUS_MAIN

    return {
        label,
        name,
        imageName,
        multiplier,
        guaranteedName,
        guaranteedMultiplier,
    }
}

export const modifyFreeBuyBonuses = (bonuses: Bonus[], bonusFeatures: BuyBonusFeature[]): FreeBuyBonusBonus[] => {
    const freeBuyBonuses = (bonuses as Bonus[]).filter((bonus) => bonus.type === BonusType.FREE_BUY_BONUS)

    for (const bonus of freeBuyBonuses) {
        const config = bonus.typeConfiguration?.freeBuyBonusConfiguration

        if (!config) continue

        const details = getFBBDetails(bonus, bonusFeatures)

        if (!details) continue

        config.label = details.label
        config.imageName = details.imageName
        config.multiplier = details.multiplier
        config.guaranteedName = details.guaranteedName
        config.guaranteedMultiplier = details.guaranteedMultiplier
    }

    return freeBuyBonuses
}

export const getCountdownValuesFromBonus = (
    bonus?: Bonus,
    forceUseSecondsFromConfig = false
): CountdownCounterValues => {
    if (!bonus || bonus.type !== BonusType.COUNTDOWN_FREE_SPIN) return null

    let totalMS = 0

    if (
        bonus.typeConfiguration?.countdownFreeSpinConfiguration?.countdownStrategy ===
        CountdownStrategy.RELATIVE_TO_PLAY_NOW
    ) {
        if (!forceUseSecondsFromConfig && bonus.expirationTime) {
            totalMS = +bonus.expirationTime - Date.now()
        } else {
            totalMS = Number(bonus.typeConfiguration?.countdownFreeSpinConfiguration?.countdownSeconds) * 1000
        }
    } else {
        const countDownDateTime = bonus.typeConfiguration?.countdownFreeSpinConfiguration?.countdownDateTime

        if (countDownDateTime) {
            totalMS = Date.parse(countDownDateTime) - Date.now()
        }
    }

    const totalSeconds = totalMS / 1000

    const { days, hours, minutes, seconds } = getCountdownUnitsFromSeconds(totalSeconds)

    if (days === 0 && hours === 0 && minutes === 0 && seconds === 0) return null

    return { days, hours, minutes, seconds }
}

/**
 * Formats a 4-items countdown array into two display pairs.
 * Automatically identifies the highest active unit and ensures a
 * two-part string output (e.g., "5 Days: 2 Hours" or "0 Min: 45 Sec").
 */
export const getCountdownDisplayItems = (items: CountdownCounterDisplayItems) => {
    const firstActiveIndex = items.findIndex((u) => u.val > 0)

    const startIndex = firstActiveIndex === -1 ? 2 : Math.min(firstActiveIndex, 2)
    const endIndex = startIndex + 1

    const firstStringDigits = `${items[startIndex].val ?? 0}`
    const firstStringLabel = ` ${items[startIndex].label} : `
    // Add 0 padding only for seconds
    const secondStringDigits =
        startIndex !== 2 ? `${items[endIndex].val ?? 0}` : `0${items[endIndex].val ?? 0}`.slice(-2)
    const secondStringLabel = ` ${items[endIndex].label}`

    return { firstStringDigits, firstStringLabel, secondStringDigits, secondStringLabel }
}
